import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Avatar, Badge, getRoleBadgeVariant } from '@/components/common';
import { cn } from '@/lib/utils';

export function ProfileDropdown() {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  if (!user) return null;

  const getDisplayRole = (role: string) => {
    switch (role) {
      case 'parent': return 'Guardian';
      case 'caretaker': return 'Caretaker';
      case 'family': return 'Family';
      case 'professional': return 'Professional';
      default: return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  const displayRole = user.role === 'professional' && user.other_role_label
    ? user.other_role_label
    : getDisplayRole(user.role);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="clickable-profile-dropdown"
        className={cn(
          'flex items-center gap-3 p-2 rounded-lg transition-colors',
          'hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background',
          isOpen && 'bg-slate-800'
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Avatar
          firstName={user.first_name}
          lastName={user.last_name}
          profilePictureUrl={user.profile_picture_url}
          role={user.role}
          size="sm"
        />
        <div className="hidden md:block text-left">
          <p className="text-sm font-medium text-white">
            {user.first_name} {user.last_name || ''}
          </p>
          <p className="text-xs text-gray-400">{displayRole}</p>
        </div>
        <svg
          className={cn(
            'w-4 h-4 text-gray-400 transition-transform hidden md:block',
            isOpen && 'rotate-180'
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div data-testid="container-profile-menu" className="absolute right-0 mt-2 w-56 bg-card rounded-xl border border-slate-700 shadow-lg py-1 z-50 animate-fade-in">
          {/* User info header */}
          <div className="px-4 py-3 border-b border-slate-700">
            <p className="text-sm font-medium text-white">
              {user.first_name} {user.last_name || ''}
            </p>
            <p className="text-xs text-gray-400 truncate">{user.email}</p>
            <div className="mt-2">
              <Badge variant={getRoleBadgeVariant(user.role)}>
                {displayRole}
              </Badge>
            </div>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <Link
              to="/settings"
              onClick={() => setIsOpen(false)}
              data-testid="clickable-profile-settings"
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:bg-slate-800 hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </Link>
          </div>

          {/* Logout */}
          <div className="border-t border-slate-700 py-1">
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              data-testid="clickable-profile-logout"
              className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-400 hover:bg-slate-800 hover:text-red-300 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Log Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileDropdown;
